import React, { useState } from "react";
import { OrganizationMember } from "@common/dto/organization/organization-member.dto";
import { PlainModal, StandardModalHeader } from "@design-system/Modal/Modal";
import { Button } from "@design-system/Button/Button";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";
import { useTransferOrganizationOwnerMutation } from "@api/organizationMember.api";
import { useActiveOrganization } from "@hooks/useActiveOrganization";
import styles from "./TransferOrganizationOwnerModal.module.scss";

interface Props {
    member: OrganizationMember;
    show: boolean;
    onHide: () => void;
}

export const TransferOrganizationOwnerModal: React.FC<Props> = ({
    member,
    show,
    onHide,
}) => {
    const activeOrganization = useActiveOrganization();
    const [emailInput, setEmailInput] = useState("");
    const [error, setError] = useState<string | null>(null);

    const transferMutation = useTransferOrganizationOwnerMutation(
        activeOrganization!.id,
    );

    const targetEmail = member.user.email ?? "";
    const isEmailConfirmed =
        !!targetEmail &&
        emailInput.trim().toLowerCase() === targetEmail.toLowerCase();

    const handleConfirm = async () => {
        if (!isEmailConfirmed) {
            return;
        }

        setError(null);

        try {
            await transferMutation.mutateAsync(member.user.id);
            handleHide();
        } catch (e) {
            const message =
                e instanceof Error
                    ? e.message
                    : "Something went wrong. Please try again.";
            setError(message);
        }
    };

    const handleHide = () => {
        setEmailInput("");
        setError(null);
        onHide();
    };

    return (
        <PlainModal show={show} onHide={handleHide}>
            <StandardModalHeader>
                Transfer organization owner role
            </StandardModalHeader>
            <VStack gap={16}>
                <p className={styles.transferOwnerWarning}>
                    You are about to transfer the organization owner role to{" "}
                    <strong>
                        {member.user.preferredName ?? member.user.email}
                    </strong>{" "}
                    ({targetEmail}).
                </p>
                <p className={styles.transferOwnerDescription}>
                    After the transfer, you will become a regular admin. This
                    action cannot be undone without the new owner transferring
                    the role back.
                </p>
                <label
                    htmlFor="transfer-owner-email-confirm"
                    className={styles.transferOwnerLabel}
                >
                    To confirm, type the email address of the new owner:
                </label>
                <input
                    id="transfer-owner-email-confirm"
                    type="email"
                    className={styles.transferOwnerInput}
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    placeholder={targetEmail}
                    autoComplete="off"
                />
                {error && <p className={styles.transferOwnerError}>{error}</p>}
                <HStack gap={8} justify="end">
                    <Button variant="secondary" size="md" onClick={handleHide}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        size="md"
                        disabled={
                            !isEmailConfirmed || transferMutation.isPending
                        }
                        onClick={handleConfirm}
                    >
                        {transferMutation.isPending
                            ? "Transferring…"
                            : "Transfer owner role"}
                    </Button>
                </HStack>
            </VStack>
        </PlainModal>
    );
};
